import { useNavigate } from '@tanstack/react-router';

import { ReachInformationCategory, ReachInformationStop } from '../../../../types/ReachInformationMessage';
import { VesselMessage } from '../../../../types/VesselMessage';
import { Button } from '../../../cdl/Button/Button';
import { Text } from '../../../cdl/Text/Text';
import { AdvancedError } from '../../../common/Error/AdvancedError';
import { HeaderWithLine } from '../../../common/HeaderWithLine/HeaderWithLine';
import { translate } from '../../../common/helpers/translate.helper';
import { LoadingIndicator } from '../../../common/LoadingIndicator/LoadingIndicator';
import { PageGrid } from '../../../common/PageGrid';
import { useSchedulesWithPrices } from '../hooks/useSchedulesWithPrices';

import { Schedule } from './components/Schedule';
import { ScheduleEmptyState } from './ScheduleEmptyState';
import { createPricesMap } from './utils/createPriceMap';

type ReachInformationMap = { [scheduleId: string]: { [category: string]: ReachInformationCategory } };

const createReachInformationMap = (reachInformation?: ReachInformationStop[]): ReachInformationMap => {
    if (!reachInformation) {
        return {};
    }

    const scheduleIdToReachInformationMap: ReachInformationMap = {};

    reachInformation.forEach((it) => {
        scheduleIdToReachInformationMap[it.scheduleId] = {};

        it.categories.forEach((category) => {
            scheduleIdToReachInformationMap[it.scheduleId][category.tankCategory] = category;
        });
    });

    return scheduleIdToReachInformationMap;
};

interface RegularScheduleSectionProps {
    vessel: VesselMessage;
}

export const RegularScheduleSection = ({ vessel }: RegularScheduleSectionProps) => {
    const navigate = useNavigate();

    const schedulesWithPricesQuery = useSchedulesWithPrices(vessel);

    const scheduleIdToReachInformationMap = createReachInformationMap(schedulesWithPricesQuery.data?.reachInformation);

    if (schedulesWithPricesQuery.isPending) {
        return <LoadingIndicator />;
    }

    if (schedulesWithPricesQuery.isError) {
        return <AdvancedError />;
    }

    if (!schedulesWithPricesQuery.data.schedules?.length) {
        return <ScheduleEmptyState />;
    }

    const pricesMap = createPricesMap(schedulesWithPricesQuery.data.prices);

    const filledScheduleList = schedulesWithPricesQuery.data.schedules.map((stop) => {
        return {
            stop,
            vesselId: vessel.id,
            prices: stop.port?.id && pricesMap[stop.port.id] ? pricesMap[stop.port.id] : null,
            reachInformation: scheduleIdToReachInformationMap[stop.id],
        };
    });

    return (
        <PageGrid>
            <HeaderWithLine>
                <Text variant="title">{translate('vessel.schedule')}</Text>
                <Button
                    onClick={() =>
                        navigate({
                            to: '/compare',
                            state: { vesselId: vessel.id, customerId: vessel.customerId },
                        })
                    }
                >
                    {translate('vessel.compareSuppliers')}
                </Button>
            </HeaderWithLine>

            <Schedule scheduleList={filledScheduleList} />
        </PageGrid>
    );
};
